/**
 * `HumanCallCard` — the human-friendly call card (ADR-0163 §1). Four
 * slots in DOM order:
 *
 *   ① kicker row — phase name, LIVE/ILLUSTRATION tag, latency (hidden
 *      when ms === 0), status chip. The tag slot is shared honesty
 *      surface: it renders in BOTH view modes (ADR-0163 §7).
 *   ② outcome line — `Phase.narration` (Geist).
 *   ③ facts — up to a handful of key/value lines DERIVED from typed
 *      sources (`lib/facts.ts`); the requester fact keeps the full
 *      documented UA + caption visible (test-pinned), and the
 *      masquerade note renders as visible amber text, never
 *      tooltip-only.
 *   ④ `‹› Show raw` inline peek — reveals the CodeCallCard in place.
 *      Per-row local state, NOT persisted; auto-opens when the call
 *      failed (`!phase.ok`) so error bodies are never hidden behind a
 *      click.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Radio } from 'lucide-react';

import { Pill } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { ThemeTokens } from '@sill/ui';
import type { Phase } from '../journeys/types.js';
import type { Fact } from '../lib/facts.js';
import { CodeCallCard } from './CodeCallCard.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/** Number of ids rendered before the "+N more" affordance takes over. */
const EXPANDABLE_SHOWN = 3;

/**
 * Value renderer for a single fact. When `expandable_ids` is present the
 * `+N more` tail becomes an inline `<button>` that toggles the full id
 * list in place; otherwise the plain `value` string renders as text.
 *
 * Local `expanded` state is intentionally per-row and NOT persisted —
 * mirrors the `‹› Show raw` peek: the summary is the default so the row
 * stays dense, expansion is opt-in per instance.
 */
function FactValueContent({ fact, C }: { fact: Fact; C: ThemeTokens }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const ids = fact.expandable_ids;
  if (ids === undefined || ids.length <= EXPANDABLE_SHOWN) {
    return <>{fact.value}</>;
  }
  const hiddenCount = ids.length - EXPANDABLE_SHOWN;
  const visible = expanded ? ids : ids.slice(0, EXPANDABLE_SHOWN);
  return (
    <>
      {`${ids.length} exposed: ${visible.join(' · ')} · `}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1,
          background: 'transparent',
          border: 'none',
          borderBottom: `1px dotted ${C.muted}`,
          cursor: 'pointer',
          padding: 0,
          textTransform: 'uppercase',
        }}
      >
        {expanded ? 'show less' : `+${hiddenCount} more`}
      </button>
    </>
  );
}

export interface HumanCallCardProps {
  phase: Phase;
  facts: readonly Fact[];
  /**
   * Omit slot ② when the narration already renders adjacent to this
   * card (the mobile unified list owns the narration line there).
   */
  hideOutcome?: boolean;
}

export function HumanCallCard({ phase: p, facts, hideOutcome = false }: HumanCallCardProps): JSX.Element {
  const C = useC();
  const status = p.endpoint.status;
  const pending = status === 0;
  const ok = status >= 200 && status < 400;
  // ④ — per-row local peek. `null` = no explicit user choice; failed
  // calls auto-open so the raw error body stays visible (test-pinned
  // "couldn't reach the surface" posture).
  const [userChoice, setUserChoice] = useState<boolean | null>(null);
  const rawOpen = userChoice ?? !p.ok;

  // Compact treatment for a factless card whose narration renders
  // outside (mobile unified list / desktop where AgentColumn carries the
  // story): a full frame with nothing in it read as broken (founder
  // feedback 2026-07-06). One slim row: kicker + tag + ms + status +
  // raw toggle; the honesty tags stay visible.
  if (facts.length === 0 && hideOutcome) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ background: C.surface2, border: `1px solid ${!pending && !ok ? C.coralFg : C.border}`, borderRadius: 2, padding: '6px 12px' }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{p.name.toUpperCase()}</span>
          {p.endpoint.live ? (
            <span className="flex items-center gap-1" style={{ fontFamily: monoFont, fontSize: 8, color: C.limeFg, letterSpacing: 1 }}>
              <Radio size={9} color={C.limeFg} /> LIVE
            </span>
          ) : (
            <span style={{ fontFamily: monoFont, fontSize: 8, color: C.dim, letterSpacing: 1 }}>ILLUSTRATION</span>
          )}
          <span className="flex-1" />
          {p.endpoint.ms !== 0 && (
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.dim }}>{`${p.endpoint.ms}ms`}</span>
          )}
          <Pill tone={pending ? 'sky' : ok ? 'lime' : 'coral'}>{pending ? '…' : String(status)}</Pill>
          <button
            onClick={() => setUserChoice(!rawOpen)}
            style={{ fontFamily: monoFont, fontSize: 8, color: C.dim, letterSpacing: 1, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, minHeight: 24 }}
          >
            {rawOpen ? '‹› Hide raw' : '‹› Show raw'}
          </button>
        </div>
        {rawOpen && <CodeCallCard phase={p} inline />}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3"
      style={{ background: C.surface2, border: `1px solid ${!pending && !ok ? C.coralFg : C.border}`, borderRadius: 2 }}
    >
      {/* ① kicker row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{p.name.toUpperCase()}</span>
        {p.endpoint.live ? (
          <span className="flex items-center gap-1" style={{ fontFamily: monoFont, fontSize: 8, color: C.limeFg, letterSpacing: 1 }}>
            <Radio size={9} color={C.limeFg} /> LIVE
          </span>
        ) : (
          <span style={{ fontFamily: monoFont, fontSize: 8, color: C.dim, letterSpacing: 1 }}>ILLUSTRATION</span>
        )}
        <span className="flex-1" />
        {p.endpoint.ms !== 0 && (
          <span style={{ fontFamily: monoFont, fontSize: 10, color: C.dim }}>{`${p.endpoint.ms}ms`}</span>
        )}
        <Pill tone={pending ? 'sky' : ok ? 'lime' : 'coral'}>{pending ? '…' : String(status)}</Pill>
      </div>

      {/* ② outcome line */}
      {!hideOutcome && (
        <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5, marginTop: 8 }}>{p.narration}</div>
      )}

      {/* ③ facts */}
      {facts.length > 0 && (
        <div className="mt-2 space-y-1">
          {facts.map((f) => (
            <div key={f.label}>
              <div className="flex items-baseline gap-2">
                <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1, textTransform: 'uppercase', flexShrink: 0 }}>
                  {f.label}
                </span>
                <span
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: f.tone === 'amber' ? C.amberFg : C.text,
                    lineHeight: 1.4,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  <FactValueContent fact={f} C={C} />
                </span>
              </div>
              {f.caption !== undefined && (
                <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, lineHeight: 1.4, marginTop: 1 }}>{f.caption}</div>
              )}
              {f.detail !== undefined && (
                <div style={{ fontFamily: monoFont, fontSize: 9, color: C.dim, lineHeight: 1.4, marginTop: 1, wordBreak: 'break-all' }}>
                  {f.detail}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ④ show-raw inline peek — deliberately quiet (8px, dim). */}
      <button
        onClick={() => setUserChoice(!rawOpen)}
        className="mt-2"
        style={{ fontFamily: monoFont, fontSize: 8, color: C.dim, letterSpacing: 1, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, minHeight: 24 }}
      >
        {rawOpen ? '‹› Hide raw' : '‹› Show raw'}
      </button>
      {rawOpen && <CodeCallCard phase={p} inline />}
    </motion.div>
  );
}
